import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ExportButton } from '@/components/ExportButton';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, Plus, Search, Edit, Trash2, Phone, Mail, MapPin,
  Package, TrendingUp, X, Building2, Calendar, Eye
} from 'lucide-react';

interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  area?: string;
  landmark?: string;
  buildingNumber?: string;
  floor?: string;
  apartment?: string;
  notes?: string;
  totalShipments: number;
  totalCodAmount: number;
  isActive: boolean;
  createdAt: string;
}

export default function CustomersPage() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const exportColumns = [
    { key: 'name', label: 'الاسم' },
    { key: 'phone', label: 'الهاتف' },
    { key: 'email', label: 'البريد الإلكتروني' },
    { key: 'city', label: 'المدينة' },
    { key: 'address', label: 'العنوان' },
    { key: 'totalShipments', label: 'عدد الشحنات' },
    { key: 'totalCodAmount', label: 'إجمالي COD', format: (v: any) => v ? `${v} ج.م` : '0' },
    { key: 'isActive', label: 'الحالة', format: (v: any) => v ? 'نشط' : 'غير نشط' },
    { key: 'createdAt', label: 'تاريخ الإنشاء', format: (v: any) => v ? new Date(v).toLocaleDateString('ar-EG') : '' }
  ];

  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '',
    area: '', landmark: '', buildingNumber: '', floor: '',
    apartment: '', notes: '', isActive: true
  });

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const userId = user?.id?.toString() || '';
      const userEmail = user?.email || '';
      
      console.log('[DEBUG] FULL USER OBJECT:', user);
      console.log('[DEBUG] FULL USER OBJECT:', user);
      console.log('[DEBUG] Loading customers for merchantId:', userId, 'email:', userEmail);
      
      // فحص إجباري: جلب شحنات هذا التاجر للتحقق من الصلاحية
      const shipmentsCheck = await api.get('/api/shipments?pageSize=1');
      const userShipments = shipmentsCheck.data?.data?.items || 
                           shipmentsCheck.data?.items || 
                           (Array.isArray(shipmentsCheck.data) ? shipmentsCheck.data : []);
      
      // إذا لم يكن لدى التاجر أي شحنات، أو لم يتم التعرف على هويته، نعرض قائمة فارغة
      if (userShipments.length === 0 || (!userId && !userEmail)) {
        console.log('[SECURITY] No shipments for this user, showing empty customer list');
        setCustomers([]);
        return;
      }
      
      // جلب العملاء مع الفلترة
      const response = await api.get(`/api/customers?pageSize=100&merchantEmail=${encodeURIComponent(userEmail)}`);
      
      console.log('[DEBUG] API Response:', response.data);
      if (response.data.success) {
        const data = response.data.data;
        const customersList = Array.isArray(data) ? data : (data?.items || []);
        setCustomers(customersList);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCustomer(null);
    setForm({
      name: '', email: '', phone: '', address: '', city: '',
      area: '', landmark: '', buildingNumber: '', floor: '',
      apartment: '', notes: '', isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      area: customer.area || '',
      landmark: customer.landmark || '',
      buildingNumber: customer.buildingNumber || '',
      floor: customer.floor || '',
      apartment: customer.apartment || '',
      notes: customer.notes || '',
      isActive: customer.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put('/api/customers/' + editingCustomer.id, form);
        alert('تم تحديث بيانات العميل بنجاح');
      } else {
        await api.post('/api/customers', form);
        alert('تم إضافة العميل بنجاح');
      }
      setShowModal(false);
      loadCustomers();
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حفظ بيانات العميل');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العميل؟')) return;
    try {
      await api.delete('/api/customers/' + id);
      loadCustomers();
      alert('تم حذف العميل بنجاح');
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حذف العميل');
    }
  };

  const filteredCustomers = Array.isArray(customers) ? customers.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search) ||
    (c.city || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: customers.length,
    active: customers.filter(c => c.isActive !== false).length,
    totalShipments: customers.reduce((sum, c) => sum + (c.totalShipments || 0), 0),
    totalCodAmount: customers.reduce((sum, c) => sum + (c.totalCodAmount || 0), 0)
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition';

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <Users className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>إدارة العملاء</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة قاعدة العملاء وبياناتهم</p>
            </div>
          </h1>
        </div>
        <ExportButton 
              data={customers} 
              filename="customers" 
              columns={exportColumns} 
              title="العملاء"
            />
            <Button
          onClick={openCreateModal}
          className='bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
        >
          <Plus className='w-5 h-5 ml-2' />
          <span>إضافة عميل جديد</span>
        </Button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Users className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي العملاء</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <TrendingUp className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>العملاء النشطون</p>
            <p className='text-2xl font-bold text-green-700'>{stats.active}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <Package className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي الشحنات</p>
            <p className='text-2xl font-bold text-blue-700'>{stats.totalShipments.toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <TrendingUp className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي التحصيل</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.totalCodAmount.toLocaleString()} ج.م</p>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث بالاسم، البريد، الهاتف، أو المدينة...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل العملاء...</p>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className='p-12 text-center'>
              <Users className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا يوجد عملاء</h3>
              <p className='text-gray-500'>ابدأ بإضافة عميلك الأول</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>العميل</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>بيانات التواصل</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>المدينة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الشحنات</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>التحصيل</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الحالة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-3'>
                          <div className='w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold'>
                            {(customer.name || 'C').charAt(0)}
                          </div>
                          <div>
                            <p className='font-semibold text-gray-900'>{customer.name}</p>
                            <p className='text-xs text-gray-500'>منذ {new Date(customer.createdAt).toLocaleDateString('ar-EG')}</p>
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='space-y-1'>
                          <div className='flex items-center gap-2 text-sm text-gray-700'>
                            <Phone className='w-3.5 h-3.5 text-gray-400' />
                            <span>{customer.phone || '-'}</span>
                          </div>
                          <div className='flex items-center gap-2 text-sm text-gray-700'>
                            <Mail className='w-3.5 h-3.5 text-gray-400' />
                            <span>{customer.email || '-'}</span>
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-2 text-sm text-gray-700'>
                          <MapPin className='w-4 h-4 text-gray-400' />
                          <span>{customer.city || '-'}</span>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-gray-900'>{customer.totalShipments || 0}</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-orange-600'>{(customer.totalCodAmount || 0).toLocaleString()} ج.م</span>
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + (
                          customer.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                        )}>
                          {customer.isActive !== false ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1'>
                          <button
                            onClick={() => { setSelectedCustomer(customer); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => openEditModal(customer)}
                            className='p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition'
                            title='تعديل'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(customer.id)}
                            className='p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition'
                            title='حذف'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>
                  {editingCustomer ? 'تعديل بيانات العميل' : 'إضافة عميل جديد'}
                </h2>
                <button onClick={() => setShowModal(false)} className='p-2 hover:bg-gray-100 rounded-lg transition'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>الاسم الكامل *</Label>
                    <Input
                      value={form.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='أدخل اسم العميل'
                    />
                  </div>
                  <div>
                    <Label>البريد الإلكتروني *</Label>
                    <Input
                      type='email'
                      value={form.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, email: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='customer@example.com'
                    />
                  </div>
                  <div>
                    <Label>رقم الهاتف *</Label>
                    <Input
                      value={form.phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, phone: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='01xxxxxxxxx'
                    />
                  </div>
                  <div>
                    <Label>المدينة</Label>
                    <Input
                      value={form.city}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, city: e.target.value })}
                      className={inputClass}
                      placeholder='المدينة'
                    />
                  </div>
                  <div>
                    <Label>المنطقة</Label>
                    <Input
                      value={form.area}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, area: e.target.value })}
                      className={inputClass}
                      placeholder='المنطقة'
                    />
                  </div>
                  <div>
                    <Label>المعلم المميز</Label>
                    <Input
                      value={form.landmark}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, landmark: e.target.value })}
                      className={inputClass}
                      placeholder='معلم مميز قريب'
                    />
                  </div>
                  <div>
                    <Label>رقم المبنى</Label>
                    <Input
                      value={form.buildingNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, buildingNumber: e.target.value })}
                      className={inputClass}
                      placeholder='رقم المبنى'
                    />
                  </div>
                  <div>
                    <Label>الطابق</Label>
                    <Input
                      value={form.floor}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, floor: e.target.value })}
                      className={inputClass}
                      placeholder='الطابق'
                    />
                  </div>
                  <div>
                    <Label>رقم الشقة</Label>
                    <Input
                      value={form.apartment}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, apartment: e.target.value })}
                      className={inputClass}
                      placeholder='رقم الشقة'
                    />
                  </div>
                  <div className='flex items-center gap-2 pt-6'>
                    <input
                      type='checkbox'
                      id='isActive'
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className='w-4 h-4'
                    />
                    <label htmlFor='isActive' className='text-sm font-medium'>عميل نشط</label>
                  </div>
                </div>
                <div>
                  <Label>العنوان التفصيلي</Label>
                  <textarea
                    value={form.address}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, address: e.target.value })}
                    className={inputClass}
                    placeholder='الشارع، المبنى، الطابق، الشقة'
                    rows={2}
                  />
                </div>
                <div>
                  <Label>ملاحظات</Label>
                  <textarea
                    value={form.notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, notes: e.target.value })}
                    className={inputClass}
                    placeholder='ملاحظات إضافية عن العميل...'
                    rows={2}
                  />
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>إلغاء</Button>
                  <Button type='submit' className='flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'>
                    {editingCustomer ? 'تحديث البيانات' : 'إضافة العميل'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedCustomer && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل العميل</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center gap-4 pb-4 border-b'>
                  <div className='w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-2xl font-bold'>
                    {(selectedCustomer.name || 'C').charAt(0)}
                  </div>
                  <div>
                    <h3 className='text-xl font-bold'>{selectedCustomer.name}</h3>
                    <p className='text-sm text-gray-500'>
                      {selectedCustomer.isActive !== false ? 'عميل نشط' : 'عميل غير نشط'} منذ {new Date(selectedCustomer.createdAt).toLocaleDateString('ar-EG')}
                    </p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_البريد الإلكتروني *</p>
                    <p className='font-semibold'>{selectedCustomer.email || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_رقم الهاتف *</p>
                    <p className='font-semibold'>{selectedCustomer.phone || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المدينة</p>
                    <p className='font-semibold'>{selectedCustomer.city || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المنطقة</p>
                    <p className='font-semibold'>{selectedCustomer.area || '-'}</p>
                  </div>
                  <div className='col-span-2'>
                    <p className='text-sm text-gray-500'>VIEW_العنوان التفصيلي</p>
                    <p className='font-semibold'>{selectedCustomer.address || '-'}</p>
                  </div>
                  {selectedCustomer.landmark && (
                    <div>
                      <p className='text-sm text-gray-500'>VIEW_المعلم المميز</p>
                      <p className='font-semibold'>{selectedCustomer.landmark}</p>
                    </div>
                  )}
                  {selectedCustomer.buildingNumber && (
                    <div>
                      <p className='text-sm text-gray-500'>VIEW_رقم المبنى</p>
                      <p className='font-semibold'>{selectedCustomer.buildingNumber}</p>
                    </div>
                  )}
                  {selectedCustomer.floor && (
                    <div>
                      <p className='text-sm text-gray-500'>VIEW_الطابق</p>
                      <p className='font-semibold'>{selectedCustomer.floor}</p>
                    </div>
                  )}
                  {selectedCustomer.apartment && (
                    <div>
                      <p className='text-sm text-gray-500'>VIEW_رقم الشقة</p>
                      <p className='font-semibold'>{selectedCustomer.apartment}</p>
                    </div>
                  )}
                  <div>
                    <p className='text-sm text-gray-500'>إجمالي الشحنات</p>
                    <p className='font-semibold text-blue-600'>{selectedCustomer.totalShipments || 0}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>إجمالي التحصيل</p>
                    <p className='font-semibold text-orange-600'>{(selectedCustomer.totalCodAmount || 0).toLocaleString()} ج.م</p>
                  </div>
                  {selectedCustomer.notes && (
                    <div className='col-span-2'>
                      <p className='text-sm text-gray-500'>VIEW_ملاحظات</p>
                      <p className='font-semibold'>{selectedCustomer.notes}</p>
                    </div>
                  )}
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedCustomer); }} className='flex-1 bg-orange-500 hover:bg-orange-600'>
                    <Edit className='w-4 h-4 ml-2' />
                    تعديل البيانات
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant='outline' className='flex-1'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
